import { getPouzivateliaApi, getSubjektApi } from '/@src/composable/useApis'
import { useAsyncState } from '@vueuse/core'
import { isRef } from 'vue'
import {
  BankoveSpojenie,
  OsobaOrganizacie,
  OsobaOrganizacieZaradenie,
  OsobaOrganizacieZaradenieZaradenieEnum,
  Subjekt,
  SubjektTypEnum,
} from '/@src/client-portal'

export { getSubjektApi }

export function useSubjektDetail(id: string) {
  return useAsyncState(
    getSubjektApi()
      .portalGetSubjektSubjekty(id)
      .then((t) => ({
        ...t.data,
        nazov: t.data?.subjektRef?.nazov,
      })),
    { id: null }
  )
}

export function useSubjektBankSpojList(id: string, q?: string) {
  return useAsyncState(
    getSubjektApi()
      .portalGetSubjektBankSpojListSubjekty(id, q)
      .then((t) => t.data),
    []
  )
}

export function isSubjektTypeOsoba(subjekt: Subjekt | Ref<Subjekt>): boolean {
  const typ = isRef(subjekt) ? subjekt.value?.typ : subjekt?.typ
  return typ?.toLowerCase() === SubjektTypEnum.Osoba?.toLowerCase()
}

export function useSubjektNav(t, pouzivatel, currentPath: string) {
  if (!pouzivatel?.subjekt?.subjektId || !pouzivatel?.pouzivatel) return []

  const subjekt = pouzivatel?.subjekt

  const isTypOsoba = isSubjektTypeOsoba(subjekt)
  const typ = isTypOsoba ? 'osoba' : 'organizacia'

  let coreLinks = [
    // { id: 'vybratInySubjekt', href: '/vyber-subjektu' },
    {
      id: 'zakladneUdajeSubjektu',
      href: `/app/subjekt/${typ}`,
      dataTestId: `user-panel-subjekt-${typ}`,
    },
    {
      id: 'kontaktneKartySubjektu',
      href: `/app/subjekt/kontakty`,
      dataTestId: 'user-panel-subjekt-kontakty',
    },
    {
      id: 'zoznamOsobSubjektu',
      href: `/app/subjekt/${typ}/osoby`,
      dataTestId: `user-panel-subjekt-${typ}-osoby`,
    },
    {
      id: 'bankoveSpojeniaSubjektu',
      href: `/app/subjekt/bankovespojenia`,
      dataTestId: `user-panel-subjekt-bankove-spojenia`,
      hasDivider: true,
    },
    {
      id: 'zakladneUdajeOsoby',
      href: `/app/subjekt/pouzivatel`,
      dataTestId: `user-panel-subjekt-pouzivatel`,
    },
    {
      id: 'kontaktneUdajeOsoby',
      href: `/app/subjekt/pouzivatel/kontakty`,
      dataTestId: `user-panel-subjekt-kontakty`,
    },
  ]

  if (isTypOsoba) {
    const isEqualSubjekt = isPouzivatelAlsoSubjekt(pouzivatel)
    coreLinks = coreLinks.filter(({ id }) =>
      isEqualSubjekt
        ? !['zoznamOsobSubjektu', 'zakladneUdajeOsoby'].includes(id)
        : !['zoznamOsobSubjektu'].includes(id)
    )
  }

  return coreLinks.map((link) => {
    const isActive = currentPath === link.href

    return {
      ...link,
      title: t(`subjekt.links.${link.id}`),
      isActive,
    }
  })
}

export function getZaradeniaOptions(t: any) {
  return Object.values(OsobaOrganizacieZaradenieZaradenieEnum).map((z) => {
    const label = t(`subjekt.zaradenia.${z}`)
    return {
      id: z,
      label,
      value: z,
    }
  })
}

export function getZaradeniaOptionsFilteredByPozadovane(t: any, pozadovane: any) {
  return Object.values(OsobaOrganizacieZaradenieZaradenieEnum)
    .map((z) => {
      const label = t(`subjekt.zaradenia.${z}`)
      return {
        id: z,
        label,
        value: z,
      }
    })
    .filter((zaradeniaObj) => !pozadovane?.includes(zaradeniaObj.value))
}

export function getPozadovaneZaradeniaOptions(t: any, zaradenia: any) {
  return zaradenia?.map((z: any) => {
    const label = t(`subjekt.zaradenia.${z}`)
    return {
      id: z,
      label,
      value: z,
    }
  })
}

export function isEntityActive(
  e: OsobaOrganizacie | OsobaOrganizacieZaradenie | BankoveSpojenie
) {
  return (
    typeof e?.meta?.casZneplatnenia !== 'string' &&
    typeof e?.meta?.platnostDo !== 'string'
  )
}

export function getActiveEntities(
  entities: Array<OsobaOrganizacieZaradenie | OsobaOrganizacie | BankoveSpojenie> = []
) {
  return entities?.filter(isEntityActive)
}

export function getActiveZaradeniaValues(zaradenia: OsobaOrganizacieZaradenie[]) {
  return getActiveEntities(zaradenia)?.map(({ zaradenie }) => zaradenie) ?? []
}

export function getPozadovaneZaradeniaValues(zaradenia: OsobaOrganizacieZaradenie[]) {
  return zaradenia?.map(({ zaradenie }) => zaradenie) ?? []
}

export function getPozadovaneZaradeniaValuesChecked(
  zaradenia: OsobaOrganizacieZaradenie[]
) {
  return zaradenia?.map((zaradenie) => zaradenie) ?? []
}

export function isPouzivatelAlsoSubjekt(pouzivatel) {
  const pouzivatelSubjektOsobaId = pouzivatel?.subjekt?.osobaId
  const pouzivatelOsobaId = pouzivatel?.pouzivatel?.osoba?.osobaId

  return pouzivatelSubjektOsobaId === pouzivatelOsobaId
}

export function updatePouzivatelData(userSession) {
  getPouzivateliaApi()
    .getCurrentProfilePortalPouzivatelia()
    .then((r) => {
      userSession.setUser(r.data)
    })
    .catch(console.error)
}
